import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export interface PublicQuoteData {
  readonly shareToken: string;
  readonly title: string;
  readonly customerName: string | null;
  readonly message: string | null;
  readonly fileName: string;
  readonly volumeCm3: number;
  readonly surfaceAreaCm2: number;
  readonly boundingBox: { readonly width: number; readonly height: number; readonly depth: number };
  readonly prices: readonly {
    readonly materialId: string;
    readonly price: number;
    readonly rawPrice: number;
    readonly estimatedDays: number;
  }[];
  readonly status: string;
  readonly expiresAt: string;
  readonly materialOptions: readonly {
    readonly materialId: string;
    readonly name: string;
    readonly description: string;
    readonly colorOptions: readonly string[];
  }[];
}

export interface PublicOrderData {
  readonly orderId: string;
  readonly type: string;
  readonly status: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly materialId: string | null;
  readonly color: string | null;
  readonly quantity: number;
  readonly productName: string | null;
  readonly totalPrice: number;
  readonly pricing: {
    readonly subtotal: number;
    readonly shippingCharge: number;
    readonly taxCharged: number;
    readonly totalCharged: number;
    readonly currency: string;
  } | null;
  readonly estimatedCompletionDate: string | null;
  readonly trackingNumber: string | null;
  readonly trackingCarrier: string | null;
  readonly deliveryStatus: string | null;
  readonly events: readonly {
    readonly status: string;
    readonly timestamp: string;
  }[];
}

/** Try to get a Cognito ID token for optional auth on public endpoints. */
const getOptionalAuthHeader = async (): Promise<Record<string, string>> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) return { Authorization: `Bearer ${token}` };
  } catch {
    // Not authenticated — that's fine
  }
  return {};
};

export const publicQuotesApi = {
  get: async (token: string): Promise<PublicQuoteData> => {
    const res = await fetch(`${API_URL}/public/quotes/${token}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.error ?? 'Quote not found');
    return body.data;
  },

  getPreviewUrl: async (token: string): Promise<string> => {
    const res = await fetch(`${API_URL}/public/quotes/${token}/preview-url`, {
      headers: { 'Content-Type': 'application/json' },
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.error ?? 'Preview unavailable');
    return body.data.previewUrl;
  },

  checkout: async (
    token: string,
    input: {
      readonly materialId: string;
      readonly color: string;
      readonly quantity: number;
      readonly customerEmail: string;
    },
  ): Promise<string> => {
    const authHeaders = await getOptionalAuthHeader();
    const res = await fetch(`${API_URL}/public/quotes/${token}/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(input),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.error ?? 'Checkout failed');
    return body.data.sessionUrl;
  },

  lookupOrders: async (email: string): Promise<readonly PublicOrderData[]> => {
    const res = await fetch(
      `${API_URL}/public/orders?email=${encodeURIComponent(email)}`,
      { headers: { 'Content-Type': 'application/json' } },
    );
    const body = await res.json();
    if (!res.ok) throw new Error(body.error ?? 'Lookup failed');
    return body.data;
  },
};
